import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ChevronDown, X } from 'lucide-react'
import { cn } from './cn'
import { SOCIAL_MEDIA } from './constants'
import { navGroups, pricingLink, type NavGroup } from './navigation'
import { ChromeLink } from './config'
import { LinkedInIcon, InstagramIcon, FacebookIcon } from './icons/social-icons'

interface Props {
  isOpen: boolean
  setIsOpen: (value: boolean) => void
}

/** DOM id for a group's panel, so its header can `aria-controls` it. */
const panelId = (label: string) => `mobile-nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`

export function MobileNav({ isOpen, setIsOpen }: Props) {
  const { pathname } = useLocation()

  /**
   * Which groups are expanded. Empty by default — the whole point.
   *
   * The five groups carry ~31 links plus four "see all" footers, every one of
   * them at `text-lg`, and all of it rendered open. The drawer opened onto a
   * wall of text you had to scroll past to find anything. Collapsed, it opens
   * onto five rows.
   *
   * Independent toggles rather than a single-open accordion: opening one group
   * should not silently shut the one you were reading, and since everything
   * starts closed the list is short regardless.
   */
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())

  // Re-collapse when the drawer closes, so it always reopens condensed rather
  // than however the previous visit left it.
  useEffect(() => {
    if (!isOpen) setExpanded(new Set())
  }, [isOpen])

  const toggle = (label: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (!next.delete(label)) next.add(label)
      return next
    })

  const linkClasses = (href: string) =>
    cn(
      'block py-1.5 transition hover:text-[oklch(50%_0.15_275)]',
      pathname === href && 'font-semibold text-[oklch(50%_0.15_275)]'
    )

  /**
   * Does this group contain the page currently open?
   *
   * Marks the header rather than auto-expanding it. Expanding would undo the
   * condensing on exactly the page you are most likely to open the menu from,
   * and "you are here" is the only thing the expansion would have told you.
   */
  const holdsCurrentPage = (group: NavGroup) =>
    group.items.some((i) => i.href === pathname) || group.footer?.href === pathname

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Menu"
      // When closed, mark inert so the off-screen menu is removed from both the
      // accessibility tree and the tab order (its links would otherwise stay
      // keyboard-focusable while invisible).
      {...(!isOpen ? { inert: '' } : {})}
      className={cn(
        'fixed inset-0 z-100 flex h-screen w-screen flex-col bg-white text-gray-900 transition-transform duration-300 ease-in-out',
        isOpen ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-full opacity-0'
      )}
    >
      <div className="flex h-full flex-col px-6 py-8">
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between">
          <span className="flex items-center gap-2">
            {/* Intrinsic ratio for space reservation — see the note in Header.tsx. */}
            <img src="/logo-mark.svg" alt="" aria-hidden="true" width={669} height={736} className="h-7 w-auto" />
            <span className="text-lg font-semibold tracking-tight">Fugue Lab</span>
          </span>
          <button onClick={() => setIsOpen(false)} aria-label="Close menu">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Grouped links (scrolls when taller than the viewport) */}
        <nav aria-label="Mobile" className="mt-8 flex-1 overflow-y-auto pb-8">
          <div className="mx-auto grid max-w-md gap-1">
            {navGroups.map((group) => {
              const open = expanded.has(group.label)
              const id = panelId(group.label)
              return (
                <div key={group.label} className="border-b border-border">
                  <button
                    type="button"
                    onClick={() => toggle(group.label)}
                    aria-expanded={open}
                    aria-controls={id}
                    // `min-h-11` is the 44px touch target; this row is now the
                    // primary way into the site on a phone, so it is sized and
                    // weighted like a nav item rather than like the small grey
                    // caption it used to be.
                    className="flex min-h-11 w-full items-center justify-between gap-3 py-2 text-left text-lg transition hover:text-[oklch(50%_0.15_275)]"
                  >
                    <span className={cn('font-medium', holdsCurrentPage(group) && 'text-[oklch(50%_0.15_275)]')}>
                      {group.label}
                    </span>
                    <ChevronDown
                      className={cn('h-5 w-5 shrink-0 text-gray-400 transition-transform', open && 'rotate-180')}
                      aria-hidden="true"
                    />
                  </button>
                  {/*
                   * `hidden` rather than a conditional render.
                   *
                   * ⚠️ NOT for SEO. The first version of this comment claimed
                   * unmounting would strip ~31 internal links from every
                   * prerendered page; that is false, and measurably so — with
                   * the panels rendering `null` while closed, every nav href is
                   * still in the static markup, because the desktop mega-menus
                   * emit the same set. Either implementation is SEO-neutral.
                   *
                   * The real reasons are smaller and honest: the links keep
                   * stable DOM identity across expand/collapse, and `[hidden]`
                   * already takes the panel out of the accessibility tree and
                   * the tab order, so no `aria-hidden`/`tabIndex` bookkeeping is
                   * needed.
                   *
                   * Keep this container free of a Tailwind `display` utility —
                   * one would override the UA `[hidden] { display: none }` and
                   * the panel would never close.
                   */}
                  <div id={id} hidden={!open} className="space-y-0.5 pb-3 text-lg">
                    {group.items.map((item) => (
                      <ChromeLink
                        key={item.href}
                        href={item.href}
                        onClick={() => setIsOpen(false)}
                        className={linkClasses(item.href)}
                      >
                        {item.label}
                      </ChromeLink>
                    ))}
                    {group.footer && (
                      <ChromeLink
                        href={group.footer.href}
                        onClick={() => setIsOpen(false)}
                        className={cn(linkClasses(group.footer.href), 'text-gray-500')}
                      >
                        {group.footer.label}
                      </ChromeLink>
                    )}
                  </div>
                </div>
              )
            })}

            <div className="mt-6 space-y-3">
              <ChromeLink
                href={pricingLink.href}
                onClick={() => setIsOpen(false)}
                className={cn('block text-lg font-medium', linkClasses(pricingLink.href))}
              >
                {pricingLink.label}
              </ChromeLink>
              <ChromeLink
                href="/get-started"
                onClick={() => setIsOpen(false)}
                className="inline-block rounded bg-black px-6 py-3 text-lg text-white transition hover:bg-gray-800"
              >
                Get Started
              </ChromeLink>
            </div>

            <div className="flex gap-4">
              <a
                href={SOCIAL_MEDIA.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="text-muted-foreground hover:text-primary inline-flex items-center gap-2"
              >
                <LinkedInIcon className="h-5 w-5" />
              </a>
              <a
                href={SOCIAL_MEDIA.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-muted-foreground hover:text-primary inline-flex items-center gap-2"
              >
                <InstagramIcon className="h-5 w-5" />
              </a>
              <a
                href={SOCIAL_MEDIA.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="text-muted-foreground hover:text-primary inline-flex items-center gap-2"
              >
                <FacebookIcon className="h-5 w-5" />
              </a>
            </div>
          </div>
        </nav>

        {/* Footer */}
        <div className="text-muted-foreground flex-shrink-0 border-t border-gray-200 pt-4 text-center text-sm">
          &copy; {new Date().getFullYear()} Fugue Lab
        </div>
      </div>
    </div>
  )
}
